import * as THREE from "three";

/**
 * Sky dome.
 *
 * A single flat background colour is the last thing in frame that gives the
 * render away: the references have a gradient from a deep zenith down to a pale
 * horizon, with soft cloud in it, and the horizon colour is what the distance
 * fog has to blend into. Painting the fog toward a flat blue leaves every
 * far-off hill sitting on a colour the sky never actually has.
 *
 * Drawn as an inside-out sphere on its own render order with depth writing off,
 * so it costs one draw call and never occludes terrain.
 */
const VERTEX_SHADER = `
precision highp float;
in vec3 position;
uniform mat4 modelViewMatrix;
uniform mat4 projectionMatrix;
out vec3 vDirection;
void main() {
  vDirection = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const FRAGMENT_SHADER = `
precision highp float;
uniform vec3 horizonColor;
uniform vec3 zenithColor;
uniform vec3 sunDirection;
uniform vec3 sunColor;
uniform float time;
in vec3 vDirection;
out vec4 outColor;

// Value noise on the sky's own plane, enough for soft cloud banks.
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x),
             mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
}
float clouds(vec2 p) {
  float value = 0.0;
  float amplitude = 0.5;
  for (int octave = 0; octave < 4; octave += 1) {
    value += noise(p) * amplitude;
    p *= 2.03;
    amplitude *= 0.5;
  }
  return value;
}

void main() {
  vec3 direction = normalize(vDirection);
  float height = clamp(direction.y, 0.0, 1.0);
  vec3 sky = mix(horizonColor, zenithColor, pow(height, 0.65));

  // Warm the sky toward the sun, which is most of what places the light.
  float toSun = max(0.0, dot(direction, normalize(sunDirection)));
  sky += sunColor * pow(toSun, 6.0) * 0.28;

  // Cloud only above the horizon, thinning as it approaches it so the band
  // does not smear along the skyline.
  if (direction.y > 0.02) {
    vec2 plane = direction.xz / max(0.12, direction.y);
    float cover = clouds(plane * 0.9 + vec2(time * 0.006, time * 0.004));
    float band = smoothstep(0.52, 0.78, cover) * smoothstep(0.0, 0.32, direction.y);
    sky = mix(sky, mix(vec3(1.0), sunColor, 0.35), band * 0.75);
  }
  outColor = vec4(sky, 1.0);
}`;

export class Sky {
  readonly mesh: THREE.Mesh;
  private readonly material: THREE.RawShaderMaterial;

  constructor(radius: number) {
    this.material = new THREE.RawShaderMaterial({
      glslVersion: THREE.GLSL3,
      vertexShader: VERTEX_SHADER,
      fragmentShader: FRAGMENT_SHADER,
      uniforms: {
        horizonColor: { value: new THREE.Color(0xbcd2e4) },
        zenithColor: { value: new THREE.Color(0x5b8fc4) },
        sunDirection: { value: new THREE.Vector3(0.46, 0.72, 0.52) },
        sunColor: { value: new THREE.Color(0xffeeda) },
        time: { value: 0 },
      },
      side: THREE.BackSide,
      depthWrite: false,
      depthTest: false,
      fog: false,
    });
    this.mesh = new THREE.Mesh(new THREE.SphereGeometry(radius, 24, 16), this.material);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1;
  }

  setSun(direction: THREE.Vector3, color: THREE.Color): void {
    this.material.uniforms["sunDirection"]!.value.copy(direction);
    this.material.uniforms["sunColor"]!.value.copy(color);
  }

  setColors(horizon: THREE.Color, zenith: THREE.Color): void {
    this.material.uniforms["horizonColor"]!.value.copy(horizon);
    this.material.uniforms["zenithColor"]!.value.copy(zenith);
  }

  setTime(seconds: number): void {
    this.material.uniforms["time"]!.value = seconds;
  }

  /** Keeps the dome centred on the viewer so it never clips the far plane. */
  follow(camera: THREE.Camera): void {
    camera.getWorldPosition(this.mesh.position);
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    this.material.dispose();
  }
}
